/**
 * useAEOOnboardingWizard
 *
 * Custom hook for managing AEO onboarding wizard state.
 * Handles polling, step execution, and state management.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '@/utils/auth-fetch';

// Types
export interface WizardStep {
  id: string;
  step_key: string;
  step_order: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  label: string;
  description: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  retry_count: number;
  quality_score: number | null;
  output_data?: Record<string, unknown>;
  updated_at: string | null;
}

export interface WizardProgress {
  completed: number;
  running: number;
  failed: number;
  pending: number;
  total: number;
  percentage: number;
}

export interface WizardAgentContext {
  sampledProducts?: Array<{
    category: string;
    productCount: number;
    samples: Array<{ name: string; brand?: string }>;
  }>;
  contextSummary?: string;
  detectedSector?: {
    id: string;
    name: string;
    description?: string;
  };
  totalCategories?: number;
  totalProducts?: number;
}

export interface WizardState {
  wizardId: string;
  state: 'in_progress' | 'completed' | 'cancelled' | 'failed';
  currentStep: string;
  currentStepLabel: string;
  currentStepOrder: number;
  progress: WizardProgress;
  steps: WizardStep[];
  startedAt: string;
  updatedAt: string;
  totalLlmCostUsd: number;
  agentContext?: WizardAgentContext;
}

export interface StepExecutionResult {
  success: boolean;
  stepKey: string;
  result?: Record<string, unknown>;
  error?: string;
  reason?: string;
  durationMs?: number;
  nextStep?: string | null;
}

interface UseAEOOnboardingWizardOptions {
  siteId: string;
  pollInterval?: number; // ms
  autoStart?: boolean;
}

// Crawl progress interface
export interface CrawlProgress {
  status: string;
  pagesCrawled: number;
  productsFound: number;
  maxPages: number;
  currentUrl?: string;
}

interface UseAEOOnboardingWizardReturn {
  wizard: WizardState | null;
  isLoading: boolean;
  error: string | null;
  isPolling: boolean;
  crawlProgress: CrawlProgress | null;

  // Actions
  startWizard: () => Promise<void>;
  cancelWizard: () => Promise<void>;
  executeStep: (stepKey: string, input?: Record<string, unknown>) => Promise<StepExecutionResult>;
  retryStep: (stepKey: string, input?: Record<string, unknown>) => Promise<StepExecutionResult>;
  refresh: () => Promise<void>;

  // Step-specific shortcuts
  setWebsiteUrl: (url: string) => Promise<StepExecutionResult>;
  selectDataSource: (type: 'feed' | 'crawl') => Promise<StepExecutionResult>;
  importFeedUrl: (feedUrl: string, feedFormat?: string) => Promise<StepExecutionResult>;
  importFeedFile: (file: File, feedFormat?: string) => Promise<StepExecutionResult>;
  startCrawl: () => Promise<StepExecutionResult>;
}

export function useAEOOnboardingWizard({
  siteId,
  pollInterval = 3000,
  autoStart = false
}: UseAEOOnboardingWizardOptions): UseAEOOnboardingWizardReturn {
  const [wizard, setWizard] = useState<WizardState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [crawlProgress, setCrawlProgress] = useState<CrawlProgress | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const crawlPollRef = useRef<NodeJS.Timeout | null>(null);
  const isExecutingRef = useRef(false);

  // Fetch wizard state
  const fetchWizard = useCallback(async (): Promise<WizardState | null> => {
    // Don't fetch if siteId is empty
    if (!siteId) {
      return null;
    }

    try {
      const response = await authFetch(`/api/aeo-wizard/site/${siteId}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch wizard');
      }
      const data = await response.json();
      return data.data;
    } catch (err) {
      console.error('Failed to fetch wizard:', err);
      throw err;
    }
  }, [siteId]);

  // Refresh wizard state
  const refresh = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchWizard();
      setWizard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh');
    }
  }, [fetchWizard]);

  // Start wizard
  const startWizard = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await authFetch('/api/aeo-wizard/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to start wizard');
      }

      const data = await response.json();
      setWizard(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start wizard');
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  // Cancel wizard
  const cancelWizard = useCallback(async () => {
    if (!wizard) return;

    try {
      const response = await authFetch(`/api/aeo-wizard/${wizard.wizardId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel wizard');
      }

      const data = await response.json();
      setWizard(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel wizard');
    }
  }, [wizard]);

  // Execute step
  const executeStep = useCallback(async (
    stepKey: string,
    input: Record<string, unknown> = {}
  ): Promise<StepExecutionResult> => {
    if (!wizard) {
      return { success: false, stepKey, error: 'No active wizard' };
    }

    try {
      isExecutingRef.current = true;

      const response = await authFetch(
        `/api/aeo-wizard/${wizard.wizardId}/step/${stepKey}/execute`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input)
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // If wizard not found, reset wizard state to show start screen
        if (response.status === 404 || data.code === 'WIZARD_NOT_FOUND') {
          console.log('[AEOWizard] Wizard not found, resetting state');
          setWizard(null);
          setError(null);
        }
        return {
          success: false,
          stepKey,
          error: data.error || 'Step execution failed'
        };
      }

      // Refresh wizard state
      await refresh();

      return data.data as StepExecutionResult;
    } catch (err) {
      return {
        success: false,
        stepKey,
        error: err instanceof Error ? err.message : 'Step execution failed'
      };
    } finally {
      isExecutingRef.current = false;
    }
  }, [wizard, refresh]);

  // Retry step
  const retryStep = useCallback(async (
    stepKey: string,
    input: Record<string, unknown> = {}
  ): Promise<StepExecutionResult> => {
    if (!wizard) {
      return { success: false, stepKey, error: 'No active wizard' };
    }

    try {
      isExecutingRef.current = true;

      const response = await authFetch(
        `/api/aeo-wizard/${wizard.wizardId}/step/${stepKey}/retry`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input)
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // If wizard not found, reset wizard state to show start screen
        if (response.status === 404 || data.code === 'WIZARD_NOT_FOUND') {
          console.log('[AEOWizard] Wizard not found, resetting state');
          setWizard(null);
          setError(null);
        }
        return {
          success: false,
          stepKey,
          error: data.error || 'Retry failed'
        };
      }

      // Refresh wizard state
      await refresh();

      return data.data as StepExecutionResult;
    } catch (err) {
      return {
        success: false,
        stepKey,
        error: err instanceof Error ? err.message : 'Retry failed'
      };
    } finally {
      isExecutingRef.current = false;
    }
  }, [wizard, refresh]);

  // Step-specific shortcuts
  const setWebsiteUrl = useCallback(async (url: string): Promise<StepExecutionResult> => {
    return executeStep('website_url', { websiteUrl: url });
  }, [executeStep]);

  const importFeedUrl = useCallback(async (
    feedUrl: string,
    feedFormat = 'auto'
  ): Promise<StepExecutionResult> => {
    return executeStep('feed_import', { feedUrl, feedFormat });
  }, [executeStep]);

  const importFeedFile = useCallback(async (
    file: File,
    feedFormat = 'auto'
  ): Promise<StepExecutionResult> => {
    if (!wizard) {
      return { success: false, stepKey: 'feed_import', error: 'No active wizard' };
    }

    try {
      isExecutingRef.current = true;

      const formData = new FormData();
      formData.append('feedFile', file);
      formData.append('feedFormat', feedFormat);

      const response = await authFetch(
        `/api/aeo-wizard/${wizard.wizardId}/feed`,
        {
          method: 'POST',
          body: formData
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // If wizard not found, reset wizard state to show start screen
        if (response.status === 404 || data.code === 'WIZARD_NOT_FOUND') {
          console.log('[AEOWizard] Wizard not found, resetting state');
          setWizard(null);
          setError(null);
        }
        return {
          success: false,
          stepKey: 'feed_import',
          error: data.error || 'File upload failed'
        };
      }

      await refresh();
      return data.data as StepExecutionResult;
    } catch (err) {
      return {
        success: false,
        stepKey: 'feed_import',
        error: err instanceof Error ? err.message : 'File upload failed'
      };
    } finally {
      isExecutingRef.current = false;
    }
  }, [wizard, refresh]);

  // Select data source (feed or crawl)
  const selectDataSource = useCallback(async (
    type: 'feed' | 'crawl'
  ): Promise<StepExecutionResult> => {
    if (!wizard) {
      return { success: false, stepKey: 'data_source_selection', error: 'No active wizard' };
    }

    try {
      isExecutingRef.current = true;

      const response = await authFetch(
        `/api/aeo-wizard/${wizard.wizardId}/data-source`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataSourceType: type })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404 || data.code === 'WIZARD_NOT_FOUND') {
          setWizard(null);
          setError(null);
        }
        return {
          success: false,
          stepKey: 'data_source_selection',
          error: data.error || 'Failed to select data source'
        };
      }

      await refresh();
      return data.data as StepExecutionResult;
    } catch (err) {
      return {
        success: false,
        stepKey: 'data_source_selection',
        error: err instanceof Error ? err.message : 'Failed to select data source'
      };
    } finally {
      isExecutingRef.current = false;
    }
  }, [wizard, refresh]);

  // Start website crawl
  const startCrawl = useCallback(async (): Promise<StepExecutionResult> => {
    if (!wizard) {
      return { success: false, stepKey: 'crawl_extract', error: 'No active wizard' };
    }

    try {
      isExecutingRef.current = true;

      const response = await authFetch(
        `/api/aeo-wizard/${wizard.wizardId}/crawl`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}'
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404 || data.code === 'WIZARD_NOT_FOUND') {
          setWizard(null);
          setError(null);
        }
        return {
          success: false,
          stepKey: 'crawl_extract',
          error: data.error || 'Failed to start crawl'
        };
      }

      await refresh();
      return data.data as StepExecutionResult;
    } catch (err) {
      return {
        success: false,
        stepKey: 'crawl_extract',
        error: err instanceof Error ? err.message : 'Failed to start crawl'
      };
    } finally {
      isExecutingRef.current = false;
    }
  }, [wizard, refresh]);

  // Fetch crawl progress
  const fetchCrawlProgress = useCallback(async () => {
    if (!wizard || wizard.currentStep !== 'crawl_extract') return;

    try {
      const response = await authFetch(`/api/aeo-wizard/${wizard.wizardId}/crawl-progress`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCrawlProgress(data.data);
        }
      }
    } catch (err) {
      console.error('[AEOWizard] Error fetching crawl progress:', err);
    }
  }, [wizard]);

  // Poll crawl progress when on crawl_extract step
  useEffect(() => {
    if (wizard?.currentStep === 'crawl_extract' && wizard?.state === 'in_progress') {
      fetchCrawlProgress();
      crawlPollRef.current = setInterval(fetchCrawlProgress, 3000);
    } else {
      setCrawlProgress(null);
      if (crawlPollRef.current) {
        clearInterval(crawlPollRef.current);
        crawlPollRef.current = null;
      }
    }

    return () => {
      if (crawlPollRef.current) {
        clearInterval(crawlPollRef.current);
      }
    };
  }, [wizard?.currentStep, wizard?.state, fetchCrawlProgress]);

  // Initial load
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await fetchWizard();
        setWizard(data);

        // Auto-start if no wizard exists
        if (!data && autoStart) {
          await startWizard();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load wizard');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [siteId, autoStart, fetchWizard, startWizard]);

  // Polling for running steps
  useEffect(() => {
    const hasRunningStep = wizard?.steps.some(s => s.status === 'running');
    const isInProgress = wizard?.state === 'in_progress';

    if (hasRunningStep && isInProgress && !isExecutingRef.current) {
      setIsPolling(true);

      pollIntervalRef.current = setInterval(async () => {
        if (!isExecutingRef.current) {
          await refresh();
        }
      }, pollInterval);
    } else {
      setIsPolling(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [wizard, pollInterval, refresh]);

  return {
    wizard,
    isLoading,
    error,
    isPolling,
    crawlProgress,
    startWizard,
    cancelWizard,
    executeStep,
    retryStep,
    refresh,
    setWebsiteUrl,
    selectDataSource,
    importFeedUrl,
    importFeedFile,
    startCrawl
  };
}
